import React, { useState, useEffect } from "react";
import ValidatorBatch from "../components/ValidatorBatch";
import * as WidgetUtils from "./logic/WidgetUtils";
import { DefaultLabel } from "../components/DefaultLabel";

// Externals
import cn from "classnames";

// Styles
import classes from "./WidgetBase.module.scss";

export default function WidgetBase(props) {
  const { WidgetComponent, ui, viewportData, createChildWidget, writeBack, doAction, startWork, getAutocompleteSuggestions, getContextActions, dataSegment, sessionId } = props;

  const [isDirty, setIsDirty] = useState(false);
  const [config, setConfig] = useState(null);

  const widgetData = viewportData.find(widget => widget.widgetRef === ui.id);

  useEffect(() => {
    //console.log("Parsing config of " + ui.id + "(" + ui.widgetType + "): ", ui.widgetConfiguration);
    let config = ui && ui.widgetConfiguration ? WidgetUtils.safeParseJSONString(ui.widgetConfiguration) : null;
    console.log("Parsed config of " + ui.id + " (" + ui.widgetType + "Widget): ", config);
    setConfig(config);
  }, [ui.widgetConfiguration]);

  useEffect(() => {
    setIsDirty(false);
  }, [widgetData]);

  let isLabelTop = ui.labelAlignment === "Top";
  let isLabelLeft = ui.labelAlignment === "Left";
  let isLabelFloating = ui.labelAlignment === "Floating";
  let showLabel = (isLabelTop || isLabelLeft) && ui.label !== ":";

  const widgetMarginStyle = () => ({
    paddingLeft: ui.leftMargin + "em",
    paddingRight: ui.rightMargin + "em",
    paddingTop: ui.topMargin + "em",
    paddingBottom: ui.bottomMargin + "em",
    height: "100%"
  });

  const availableSpaceGridStyle = () => ({
    gridTemplateRows: toSizeDefinition(ui.height, ui.takeAdditionalVerticalSpace),
    gridTemplateColumns: toSizeDefinition(ui.width, ui.takeAdditionalHorizontalSpace),

    alignItems: toGridAlignment(ui.controlVerticalAlignment),
    justifyItems: toGridAlignment(ui.controlHorizontalAlignment)
  });

  const widgetGridStyle = () => ({
    gridTemplateRows: labelRowSize() + " " + widgetRowSize(),
    gridTemplateColumns: labelColumnSize() + " " + widgetColumnSize() + " " + validatorColumnSize(),
  });

  const toGridAlignment = alignment => {
    switch (alignment) {
      case "Left":
        return "start";
      case "Right":
        return "end";
      case "Center":
        return "center";
      default:
        return "stretch";
    }
  };

  const labelRowSize = () => ui.verticalAlignmentOffset + "em";
  const widgetRowSize = () => toSizeDefinition(ui.controlHeight, ui.controlVerticalAlignment === "Stretch");

  const labelColumnSize = () => ui.horizontalAlignmentOffset + "em";
  const widgetColumnSize = () => toSizeDefinition(ui.controlWidth, ui.controlHorizontalAlignment === "Stretch");
  const validatorColumnSize = () => "0em";

  const toSizeDefinition = (size, takeSpace) => {
    if (takeSpace) {
      return "minmax(" + size + "em, 100fr)";
    } else return size + "em";
  };

  const renderLabelComponent = () => (
    <DefaultLabel value={WidgetUtils.toCleanText(ui.label)} height={ui.controlHeight} />
  );

  const renderValidatorComponent = () => {
    const type = widgetData ? widgetData.validatorSeverity : null;
    const text = widgetData ? widgetData.validatorMessage : null;
    return <ValidatorBatch type={type} text={text} />
  }

  const handleStartWork = async () => {
    console.log("StartWork " + (isDirty === true ? "skipped" : "triggered") + " for Widget: " + ui.id);
    if (isDirty === true) return;
    await startWork(ui.id).then(result => {
      return result;
    });
    setIsDirty(true);
  };

  const handleWriteBack = async (value, force) => {
    console.log("WriteBack triggered for Widget: " + ui.id + " with isDirty: " + isDirty + " and force: " + force + " and content: " + value);
    if (!force && isDirty !== true) return false;
    await writeBack(ui.id, value).then(result => {
      return result;
    });
    setIsDirty(false);
  };

  const handleDoAction = async (actionId, content) => {
    console.log("DoAction triggered for Widget: " + ui.id + " with actionId:", actionId);
    return await doAction(ui.id, actionId, content).then(result => {
      return result;
    });
  };

  const handleGetContextActions = async () => {
    console.log("GetContextActions triggered for Widget: " + ui.id);
    return await getContextActions(ui.id).then(result => {
      return result.actionGroups.map(g => {
        return {
          "text": g.displayText,
          "items": g.actions.map(
            a => {
              return {
                "action": () => doAction(ui.id, a.actionId, a.content),
                "text": a.displayText
              }
            })
        }
      });
    });
  };

  return (
    <div style={widgetMarginStyle()}>
      <div className={classes.wrapper} style={availableSpaceGridStyle()}>
        <div className={classes.wrapper} style={widgetGridStyle()}>
          {showLabel && isLabelTop ? <div className={cn(classes.box, classes.b)}>{renderLabelComponent()}</div> : <React.Fragment />}
          {showLabel && isLabelLeft ? <div className={cn(classes.box, classes.c)}>{renderLabelComponent()}</div> : <React.Fragment />}
          <div className={cn(classes.box, classes.d)}>
            <WidgetComponent
              ui={ui}
              data={widgetData}
              config={config}
              createChildWidget={createChildWidget}
              writeBack={handleWriteBack}
              doAction={handleDoAction}
              startWork={handleStartWork}
              getAutocompleteSuggestions={getAutocompleteSuggestions}
              dataSegment={dataSegment}
              sessionId={sessionId}
              getContextActions={ui.hasContextActions ? handleGetContextActions : null}
              useFloatingLabel={isLabelFloating}
            />
          </div>
          {widgetData && widgetData.validatorMessage ? <div className={classes.e}> {renderValidatorComponent()}</div> : <React.Fragment />}
        </div>
      </div>
    </div>
  );
}
